import { calculateLifeProportions } from "../lib/life-proportions";
import { calculateRemainingYears } from "../lib/calculate-remaining-years";
import { calculateAgeYears } from "../lib/calculate-age-years";
import { lifeBarHtml } from "../lib/life-bar-html";
import {
  SECONDS_PER_DAY,
  SECONDS_PER_HOUR,
  SECONDS_PER_MINUTE,
  SECONDS_PER_WEEK,
  SECONDS_PER_YEAR,
} from "../lib/time-units";
import {
  startCountdown,
  startElapsedCounter,
  type CountdownHandle,
  type CountdownUnit,
} from "./countdown";
import { systemClock, type Clock } from "../adapters/clock-port";
import {
  DEFAULT_HOURLY_RATE_USD,
  effectiveHourlyRate,
  valueOfTimeUsd,
  type WagePeriod,
} from "../lib/value-of-time";

const WEALTH_COUNTDOWN_UNITS: CountdownUnit[] = [
  { label: "years", perUnitSeconds: SECONDS_PER_YEAR, padTo: 4 },
  { label: "weeks", perUnitSeconds: SECONDS_PER_WEEK, padTo: 2 },
  { label: "days", perUnitSeconds: SECONDS_PER_DAY, padTo: 1 },
  { label: "hours", perUnitSeconds: SECONDS_PER_HOUR, padTo: 2 },
  { label: "minutes", perUnitSeconds: SECONDS_PER_MINUTE, padTo: 2 },
  { label: "seconds", perUnitSeconds: 1, padTo: 2 },
];

const HERO_COUNTDOWN_UNITS: CountdownUnit[] = [
  { label: "years", perUnitSeconds: SECONDS_PER_YEAR },
  { label: "days", perUnitSeconds: SECONDS_PER_DAY },
  { label: "hours", perUnitSeconds: SECONDS_PER_HOUR, padTo: 2 },
  { label: "minutes", perUnitSeconds: SECONDS_PER_MINUTE, padTo: 2 },
  { label: "seconds", perUnitSeconds: 1, padTo: 2 },
];

import type {
  Demographics,
  LifeExpectancyLookup,
  Region,
  Sex,
  UsRace,
} from "../adapters/life-expectancy-port";

const RACES: readonly UsRace[] = ["white", "black", "hispanic", "asian", "native"];
const RACE_LABELS: Record<UsRace, string> = {
  white: "White",
  black: "Black",
  hispanic: "Hispanic / Latino",
  asian: "Asian / Pacific Islander",
  native: "American Indian / Alaska Native",
};
const SEX_LABELS: Record<Sex, string> = {
  female: "Female",
  male: "Male",
};

export function mountTimeRemainingPage(
  root: HTMLElement,
  lookup?: LifeExpectancyLookup,
  clock: Clock = systemClock,
): void {
  const now = clock.now();
  const currentYear = now.getUTCFullYear();
  const defaultDobDate = new Date(
    Date.UTC(currentYear - 25, now.getUTCMonth(), now.getUTCDate()),
  );
  const defaultYear = defaultDobDate.getUTCFullYear();
  const defaultMonth = defaultDobDate.getUTCMonth() + 1;
  const defaultDay = defaultDobDate.getUTCDate();
  root.innerHTML = `
    <main class="time-remaining">
      <div
        class="countdown-container countdown-container-hero"
        data-testid="countdown-hero-container"
        aria-live="off"
      ></div>
      <div class="life-bar-container" data-testid="life-bar-container"></div>
      <h1>How much time do you have left?</h1>
      <form class="age-form">
          <fieldset class="dob-section">
            <legend>Your date of birth</legend>
            <div class="dob-row">
              <label class="dob-field">
                Year
                <input
                  name="dob-year"
                  type="number"
                  min="1900"
                  max="${currentYear}"
                  step="1"
                  value="${defaultYear}"
                  inputmode="numeric"
                  required
                />
              </label>
              <label class="dob-field">
                Month
                <input
                  name="dob-month"
                  type="number"
                  min="1"
                  max="12"
                  step="1"
                  value="${defaultMonth}"
                  inputmode="numeric"
                  required
                />
              </label>
              <label class="dob-field">
                Day
                <input
                  name="dob-day"
                  type="number"
                  min="1"
                  max="31"
                  step="1"
                  value="${defaultDay}"
                  inputmode="numeric"
                  required
                />
              </label>
              <div class="dob-age">
                <span class="dob-age-label">Age</span>
                <span class="dob-age-value" data-testid="calculated-age">—</span>
              </div>
            </div>
          </fieldset>
          <fieldset class="wage-section">
            <legend>Your wage</legend>
            <div class="wage-row">
              <label class="wage-amount-field">
                Amount
                <input
                  name="wage-amount"
                  type="number"
                  min="0"
                  step="any"
                  value="${DEFAULT_HOURLY_RATE_USD}"
                  inputmode="decimal"
                  required
                />
              </label>
              <label class="wage-period-field">
                Per
                <select name="wage-period">
                  <option value="hour" selected>hour</option>
                  <option value="year">year (salary)</option>
                </select>
              </label>
            </div>
          </fieldset>
          ${lookup ? renderDemographicFields(lookup) : ""}
          <button type="submit">Show me</button>
        </form>
      <aside class="wealth-panel" data-testid="wealth-panel">
        <div class="wealth-panel-label">
          Your wealth · <em>Time = Currency</em>
        </div>
        <div class="wealth-blocks">
          <div class="wealth-block">
            <div class="wealth-block-label">Time spent</div>
            <div
              class="wealth-readout-container"
              data-testid="wealth-spent-container"
              aria-live="off"
            ></div>
            <div class="wealth-dollars" data-testid="wealth-spent-dollars"></div>
          </div>
          <div class="wealth-block">
            <div class="wealth-block-label">Time remaining</div>
            <div
              class="wealth-readout-container"
              data-testid="wealth-remaining-container"
              aria-live="off"
            ></div>
            <div class="wealth-dollars" data-testid="wealth-remaining-dollars"></div>
          </div>
        </div>
        <div class="wealth-panel-footnote">
          Forearm-clock format from <em>In Time</em> (2011) · 1 hour ≈ $30
        </div>
      </aside>
    </main>
  `;

  const form = root.querySelector("form");
  const dobYearInput = root.querySelector<HTMLInputElement>("input[name='dob-year']");
  const dobMonthInput = root.querySelector<HTMLInputElement>("input[name='dob-month']");
  const dobDayInput = root.querySelector<HTMLInputElement>("input[name='dob-day']");
  const ageDisplay = root.querySelector<HTMLElement>("[data-testid='calculated-age']");
  const heroCountdownContainer = root.querySelector<HTMLElement>(
    "[data-testid='countdown-hero-container']",
  );
  const lifeBarContainer = root.querySelector<HTMLElement>(
    "[data-testid='life-bar-container']",
  );
  const wealthSpentContainer = root.querySelector<HTMLElement>(
    "[data-testid='wealth-spent-container']",
  );
  const wealthRemainingContainer = root.querySelector<HTMLElement>(
    "[data-testid='wealth-remaining-container']",
  );
  const wealthSpentDollars = root.querySelector<HTMLElement>(
    "[data-testid='wealth-spent-dollars']",
  );
  const wealthRemainingDollars = root.querySelector<HTMLElement>(
    "[data-testid='wealth-remaining-dollars']",
  );
  const wageAmountInput = root.querySelector<HTMLInputElement>(
    "input[name='wage-amount']",
  );
  const wagePeriodSelect = root.querySelector<HTMLSelectElement>(
    "select[name='wage-period']",
  );

  if (
    !form ||
    !dobYearInput ||
    !dobMonthInput ||
    !dobDayInput ||
    !ageDisplay ||
    !heroCountdownContainer ||
    !lifeBarContainer ||
    !wealthSpentContainer ||
    !wealthRemainingContainer ||
    !wealthSpentDollars ||
    !wealthRemainingDollars ||
    !wageAmountInput ||
    !wagePeriodSelect
  )
    return;

  let activeHeroCountdown: CountdownHandle | null = null;
  let activeWealthSpent: CountdownHandle | null = null;
  let activeWealthRemaining: CountdownHandle | null = null;

  form.addEventListener("submit", handleSubmit);
  form.addEventListener("input", recompute);
  form.addEventListener("change", recompute);

  if (lookup) {
    const countrySelect = root.querySelector<HTMLSelectElement>("select[name='region']");
    if (countrySelect) {
      countrySelect.addEventListener("change", () => refreshSubRegions(root, lookup));
      refreshSubRegions(root, lookup);
    }
  }

  recompute();

  function handleSubmit(event: SubmitEvent): void {
    event.preventDefault();
    recompute();
  }

  function recompute(): void {
    if (
      !dobYearInput ||
      !dobMonthInput ||
      !dobDayInput ||
      !ageDisplay ||
      !heroCountdownContainer ||
      !lifeBarContainer ||
      !wealthSpentContainer ||
      !wealthRemainingContainer ||
      !wealthSpentDollars ||
      !wealthRemainingDollars ||
      !wageAmountInput ||
      !wagePeriodSelect
    )
      return;
    const dob = parseDob(dobYearInput.value, dobMonthInput.value, dobDayInput.value);
    if (dob === null) return;
    const age = calculateAgeYears(dob, clock.now());
    ageDisplay.textContent = Math.floor(age).toString();

    const expectancy = lookup ? lookupExpectancy(root, lookup) : undefined;
    const proportions = calculateLifeProportions(age, expectancy);
    const years = calculateRemainingYears(age, expectancy);

    lifeBarContainer.innerHTML = lifeBarHtml(proportions);

    activeHeroCountdown?.stop();
    activeWealthSpent?.stop();
    activeWealthRemaining?.stop();
    const remainingSeconds = Math.max(0, years) * SECONDS_PER_YEAR;
    const deadline = new Date(clock.now().getTime() + remainingSeconds * 1000);
    activeHeroCountdown = startCountdown(
      heroCountdownContainer,
      deadline,
      clock,
      HERO_COUNTDOWN_UNITS,
      { testIdPrefix: "countdown-hero", variantClass: "countdown-hero" },
    );
    const wageAmount = Number(wageAmountInput.value);
    const wagePeriod: WagePeriod =
      wagePeriodSelect.value === "year" ? "year" : "hour";
    const hourlyRate = effectiveHourlyRate(wageAmount, wagePeriod);

    activeWealthRemaining = startCountdown(
      wealthRemainingContainer,
      deadline,
      clock,
      WEALTH_COUNTDOWN_UNITS,
      {
        testIdPrefix: "countdown-wealth-remaining",
        variantClass: "countdown-wealth",
        onTick: (sec) => {
          if (wealthRemainingDollars) {
            wealthRemainingDollars.textContent = valueOfTimeUsd(sec, hourlyRate);
          }
        },
      },
    );
    activeWealthSpent = startElapsedCounter(
      wealthSpentContainer,
      dob,
      clock,
      WEALTH_COUNTDOWN_UNITS,
      {
        testIdPrefix: "countdown-wealth-spent",
        variantClass: "countdown-wealth",
        onTick: (sec) => {
          if (wealthSpentDollars) {
            wealthSpentDollars.textContent = valueOfTimeUsd(sec, hourlyRate);
          }
        },
      },
    );
  }
}

function renderDemographicFields(lookup: LifeExpectancyLookup): string {
  const countryOptions = lookup
    .regions()
    .filter((r) => r.kind === "country")
    .map((r) => `<option value="${r.code}">${escape(r.label)}</option>`)
    .join("");
  const sexOptions = (Object.keys(SEX_LABELS) as Sex[])
    .map((s) => `<option value="${s}">${SEX_LABELS[s]}</option>`)
    .join("");
  const raceOptions = [`<option value="">Prefer not to say</option>`]
    .concat(RACES.map((r) => `<option value="${r}">${RACE_LABELS[r]}</option>`))
    .join("");

  return `
    <fieldset class="region-section">
      <legend>Region</legend>
      <label>
        Country
        <select name="region">${countryOptions}</select>
      </label>
      <label class="subregion-label" data-testid="subregion-label" hidden>
        State / sub-region
        <select name="subregion"></select>
      </label>
    </fieldset>
    <label>
      Sex
      <select name="sex">${sexOptions}</select>
    </label>
    <label>
      Race / Hispanic origin (US national only)
      <select name="race">${raceOptions}</select>
    </label>
  `;
}

function refreshSubRegions(root: HTMLElement, lookup: LifeExpectancyLookup): void {
  const countrySelect = root.querySelector<HTMLSelectElement>("select[name='region']");
  const subregionLabel = root.querySelector<HTMLElement>("[data-testid='subregion-label']");
  const subregionSelect = root.querySelector<HTMLSelectElement>("select[name='subregion']");
  if (!countrySelect || !subregionLabel || !subregionSelect) return;

  const countryCode = countrySelect.value;
  const subRegions = lookup
    .regions()
    .filter((r) => r.kind === "us-state" && r.code.startsWith(countryCode + "-"));

  if (subRegions.length === 0) {
    subregionLabel.hidden = true;
    subregionSelect.innerHTML = "";
    subregionSelect.value = "";
    return;
  }

  const options = [`<option value="">Whole country</option>`]
    .concat(subRegions.map((r) => `<option value="${r.code}">${escape(r.label)}</option>`))
    .join("");
  subregionSelect.innerHTML = options;
  subregionLabel.hidden = false;
}

function lookupExpectancy(root: HTMLElement, lookup: LifeExpectancyLookup): number {
  const sex = readSex(getSelectValue(root, "select[name='sex']"));
  const subregion = readOptionalSelectValue(root, "select[name='subregion']");
  const regionCode = subregion ?? getSelectValue(root, "select[name='region']");
  const region = resolveRegion(lookup, regionCode);
  const race = readRace(getSelectValue(root, "select[name='race']"));
  const demographics: Demographics = race ? { sex, region, race } : { sex, region };
  return lookup.lookup(demographics);
}

function readOptionalSelectValue(root: HTMLElement, selector: string): string | undefined {
  const el = root.querySelector<HTMLSelectElement>(selector);
  if (!el) return undefined;
  return el.value === "" ? undefined : el.value;
}

function getSelectValue(root: HTMLElement, selector: string): string {
  const el = root.querySelector<HTMLSelectElement>(selector);
  if (!el) throw new Error(`Missing select: ${selector}`);
  return el.value;
}

function readSex(value: string): Sex {
  if (value === "female" || value === "male") return value;
  throw new Error(`Invalid sex: ${value}`);
}

function readRace(value: string): UsRace | undefined {
  if (value === "") return undefined;
  if ((RACES as readonly string[]).includes(value)) return value as UsRace;
  throw new Error(`Invalid race: ${value}`);
}

function resolveRegion(lookup: LifeExpectancyLookup, code: string): Region {
  const opt = lookup.regions().find((r) => r.code === code);
  if (!opt) throw new Error(`Unknown region: ${code}`);
  return { kind: opt.kind, code: opt.code };
}

function parseDob(yearStr: string, monthStr: string, dayStr: string): Date | null {
  if (!yearStr || !monthStr || !dayStr) return null;
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

function escape(text: string): string {
  return text.replace(/[&<>"']/g, (ch) => {
    if (ch === "&") return "&amp;";
    if (ch === "<") return "&lt;";
    if (ch === ">") return "&gt;";
    if (ch === '"') return "&quot;";
    return "&#39;";
  });
}
